import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '@/hooks/useAuth'
import {
  UsersIcon,
  AcademicCapIcon,
  ClockIcon,
  TruckIcon,
  ChartBarIcon,
  CogIcon,
  BellIcon,
  PlusIcon,
  EyeIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  BookOpenIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline'
import { Button } from '@/components/ui/Button'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { getSchoolAdminDashboard, SchoolAdminDashboardData } from '@/api/dashboardApi'

// Chart colors
const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899']

const SchoolAdminDashboard: React.FC = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [dashboardData, setDashboardData] = useState<SchoolAdminDashboardData | null>(null)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    setLoading(true)
    try {
      const response = await getSchoolAdminDashboard()
      console.log('Dashboard data:', response)

      if (response.success && response.data) {
        setDashboardData(response.data)
      }
    } catch (error) {
      console.error('Dashboard error:', error)
      toast.error('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  // Extract data from dashboardData
  const totalStudents = dashboardData?.users?.students || 0
  const totalTeachers = dashboardData?.users?.teachers || 0
  const totalParents = dashboardData?.users?.parents || 0
  const totalDrivers = dashboardData?.users?.drivers || 0
  const totalUsers = dashboardData?.users?.total || 0
  const activeUsers = dashboardData?.users?.active || 0

  // Attendance stats
  const presentCount = dashboardData?.attendance?.presentCount || 0
  const absentCount = dashboardData?.attendance?.absentCount || 0
  const lateCount = dashboardData?.attendance?.lateCount || 0
  const attendanceRate = dashboardData?.attendance?.attendanceRate || 0

  // Library stats
  const totalBooks = dashboardData?.library?.totalBooks || 0
  const activeBorrowings = dashboardData?.library?.activeBorrowings || 0
  const overdueBooks = dashboardData?.library?.overdueBorrowings || 0

  // Fleet stats
  const totalVehicles = dashboardData?.fleet?.totalVehicles || 0
  const activeVehicles = dashboardData?.fleet?.activeVehicles || 0

  // Weekly library chart data from API
  const weeklyBorrowings = dashboardData?.charts?.weeklyLibrary || []

  // User distribution for pie chart
  const userDistribution = [
    { name: 'Students', value: totalStudents, color: '#3B82F6' },
    { name: 'Teachers', value: totalTeachers, color: '#10B981' },
    { name: 'Parents', value: totalParents, color: '#F59E0B' },
    { name: 'Drivers', value: totalDrivers, color: '#8B5CF6' },
  ].filter(item => item.value > 0)

  // Attendance distribution for pie chart
  const attendanceDistribution = [
    { name: 'Present', value: presentCount, color: '#10B981' },
    { name: 'Absent', value: absentCount, color: '#EF4444' },
    { name: 'Late', value: lateCount, color: '#F59E0B' },
  ].filter(item => item.value > 0)

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 bg-gray-200 rounded w-64 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mt-2 animate-pulse"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
              <div className="h-20 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome back, {user?.firstName}!
          </h1>
          <p className="text-gray-600 mt-1">
            Here's what's happening at your school today
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Link to="/schoolAdmin/settings">
            <Button variant="secondary">
              <CogIcon className="h-4 w-4 mr-2" />
              Settings
            </Button>
          </Link>
          <Link to="/schoolAdmin/users">
            <Button>
              <PlusIcon className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </Link>
        </div>
      </div>

      {/* Primary Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Students */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Students</p>
              <p className="text-2xl font-bold text-gray-900">{totalStudents.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <AcademicCapIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            <ArrowTrendingUpIcon className="h-4 w-4 text-green-600 mr-1" />
            <span className="text-green-600 font-medium">Active learners</span>
          </div>
        </motion.div>

        {/* Teachers */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Teachers</p>
              <p className="text-2xl font-bold text-gray-900">{totalTeachers}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <UsersIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            <CheckCircleIcon className="h-4 w-4 text-green-600 mr-1" />
            <span className="text-green-600 font-medium">All active</span>
          </div>
        </motion.div>

        {/* Attendance Rate */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Attendance Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {attendanceRate > 0 ? `${attendanceRate.toFixed(1)}%` : 'N/A'}
              </p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <ClockIcon className="h-6 w-6 text-orange-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            <span className="text-gray-500">
              {presentCount} present today
            </span>
          </div>
        </motion.div>

        {/* Active Vehicles */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Vehicles</p>
              <p className="text-2xl font-bold text-gray-900">{activeVehicles}/{totalVehicles}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <TruckIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            <span className="text-gray-500">On routes</span>
          </div>
        </motion.div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Link to="/schoolAdmin/parents" className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <UserGroupIcon className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{totalParents}</p>
              <p className="text-xs text-gray-600">Parents</p>
            </div>
          </div>
        </Link>
        <Link to="/schoolAdmin/drivers" className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <TruckIcon className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{totalDrivers}</p>
              <p className="text-xs text-gray-600">Drivers</p>
            </div>
          </div>
        </Link>
        <Link to="/schoolAdmin/library/books" className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-100 rounded-lg">
              <BookOpenIcon className="h-5 w-5 text-cyan-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{totalBooks}</p>
              <p className="text-xs text-gray-600">Library Books</p>
            </div>
          </div>
        </Link>
        <Link to="/schoolAdmin/library/borrowings" className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${overdueBooks > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
              {overdueBooks > 0 ? (
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
              ) : (
                <CheckCircleIcon className="h-5 w-5 text-green-600" />
              )}
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{overdueBooks}</p>
              <p className="text-xs text-gray-600">Overdue Books</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attendance Trend Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Weekly Attendance Trend</h3>
            <Link to="/schoolAdmin/attendance">
              <Button variant="secondary" size="sm">
                <EyeIcon className="h-4 w-4 mr-1" />
                View Details
              </Button>
            </Link>
          </div>
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <ClockIcon className="h-12 w-12 mb-4 text-gray-300" />
            <p>No weekly attendance data available</p>
            <Link to="/schoolAdmin/sessions" className="mt-2">
              <Button variant="secondary" size="sm">Start a Session</Button>
            </Link>
          </div>
        </motion.div>

        {/* User Distribution Pie Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">User Distribution</h3>
            <Link to="/schoolAdmin/users">
              <Button variant="secondary" size="sm">
                <UsersIcon className="h-4 w-4 mr-1" />
                Manage Users
              </Button>
            </Link>
          </div>
          <div className="flex items-center justify-center">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={userDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {userDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => [value.toLocaleString(), 'Count']}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{totalUsers.toLocaleString()}</p>
            <p className="text-sm text-gray-600">Total Users ({activeUsers} active)</p>
          </div>
        </motion.div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Library Activity Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Library Activity</h3>
            <Link to="/schoolAdmin/library/borrowings">
              <Button variant="secondary" size="sm">
                <BookOpenIcon className="h-4 w-4 mr-1" />
                View Library
              </Button>
            </Link>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={weeklyBorrowings}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="day" stroke="#9CA3AF" fontSize={12} />
              <YAxis stroke="#9CA3AF" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Bar dataKey="borrowed" fill="#3B82F6" name="Borrowed" radius={[4, 4, 0, 0]} />
              <Bar dataKey="returned" fill="#10B981" name="Returned" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 grid grid-cols-2 gap-4 text-center">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-xl font-bold text-blue-600">{activeBorrowings}</p>
              <p className="text-xs text-gray-600">Active Borrowings</p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg">
              <p className="text-xl font-bold text-red-600">{overdueBooks}</p>
              <p className="text-xs text-gray-600">Overdue</p>
            </div>
          </div>
        </motion.div>

        {/* Today's Attendance Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Attendance Overview</h3>
            <Link to="/schoolAdmin/sessions">
              <Button variant="secondary" size="sm">
                <CalendarDaysIcon className="h-4 w-4 mr-1" />
                Sessions
              </Button>
            </Link>
          </div>
          {attendanceDistribution.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={attendanceDistribution}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {attendanceDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="p-2 bg-green-50 rounded-lg">
                  <p className="text-lg font-bold text-green-600">{presentCount}</p>
                  <p className="text-xs text-gray-600">Present</p>
                </div>
                <div className="p-2 bg-red-50 rounded-lg">
                  <p className="text-lg font-bold text-red-600">{absentCount}</p>
                  <p className="text-xs text-gray-600">Absent</p>
                </div>
                <div className="p-2 bg-amber-50 rounded-lg">
                  <p className="text-lg font-bold text-amber-600">{lateCount}</p>
                  <p className="text-xs text-gray-600">Late</p>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <ClockIcon className="h-12 w-12 mb-4 text-gray-300" />
              <p>No attendance data for today</p>
              <Link to="/schoolAdmin/sessions" className="mt-2">
                <Button variant="secondary" size="sm">Start a Session</Button>
              </Link>
            </div>
          )}
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Quick Actions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
          <Link to="/schoolAdmin/users">
            <button className="w-full h-20 flex flex-col items-center justify-center space-y-2 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white transition-all shadow-md hover:shadow-lg transform hover:scale-[1.02]">
              <UsersIcon className="h-6 w-6" />
              <span className="text-xs font-medium">Add User</span>
            </button>
          </Link>
          <Link to="/schoolAdmin/sessions">
            <button className="w-full h-20 flex flex-col items-center justify-center space-y-2 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white transition-all shadow-md hover:shadow-lg transform hover:scale-[1.02]">
              <ClockIcon className="h-6 w-6" />
              <span className="text-xs font-medium">Attendance</span>
            </button>
          </Link>
          <Link to="/schoolAdmin/library/borrowings">
            <button className="w-full h-20 flex flex-col items-center justify-center space-y-2 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white transition-all shadow-md hover:shadow-lg transform hover:scale-[1.02]">
              <BookOpenIcon className="h-6 w-6" />
              <span className="text-xs font-medium">Library</span>
            </button>
          </Link>
          <Link to="/schoolAdmin/fleet">
            <button className="w-full h-20 flex flex-col items-center justify-center space-y-2 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white transition-all shadow-md hover:shadow-lg transform hover:scale-[1.02]">
              <TruckIcon className="h-6 w-6" />
              <span className="text-xs font-medium">Fleet</span>
            </button>
          </Link>
          <Link to="/schoolAdmin/wallet">
            <button className="w-full h-20 flex flex-col items-center justify-center space-y-2 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white transition-all shadow-md hover:shadow-lg transform hover:scale-[1.02]">
              <CurrencyDollarIcon className="h-6 w-6" />
              <span className="text-xs font-medium">Wallet</span>
            </button>
          </Link>
          <Link to="/schoolAdmin/analytics">
            <button className="w-full h-20 flex flex-col items-center justify-center space-y-2 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white transition-all shadow-md hover:shadow-lg transform hover:scale-[1.02]">
              <ChartBarIcon className="h-6 w-6" />
              <span className="text-xs font-medium">Reports</span>
            </button>
          </Link>
        </div>
      </motion.div>
    </div>
  )
}

export default SchoolAdminDashboard
